import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  RadioGroup,
  Radio,
  Slider,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  IconButton,
  Tooltip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/Email';
import { getAppSettings, updateAppSettings, updateAssemblyAISettings, updateOpenAISettings, updateEmailSettings } from '../../../firebase/services/appSettingsService';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import emailService from '../../../services/emailService';

import BaseButton from '../../../components/common/BaseButton';
import BaseTextField from '../../../components/common/BaseTextField';
import SettingsIcon from '@mui/icons-material/Settings';
import MicIcon from '@mui/icons-material/Mic';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SpeedIcon from '@mui/icons-material/Speed';
import TranslateIcon from '@mui/icons-material/Translate';
import SecurityIcon from '@mui/icons-material/Security';
import RestoreIcon from '@mui/icons-material/Restore';
import { useApp } from '../../../contexts/AppContext';
import BaseDialog from '../../../components/common/BaseDialog';
import {
  RecordVoiceOver as RecordVoiceOverIcon,
  Edit as EditIcon
} from '@mui/icons-material';

const TranscriptionSettings = () => {
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAssemblyAIKey, setShowAssemblyAIKey] = useState(false);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showGraphClientSecret, setShowGraphClientSecret] = useState(false);
  const [operationStatus, setOperationStatus] = useState({
    success: false,
    error: false,
    message: ''
  });

  // États pour le test d'email
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState({
    success: false,
    error: false,
    message: ''
  });

  const { showAlert } = useApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

  // Configuration du style glassmorphisme
  const glassBoxSx = {
    background: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(248,250,255,0.15) 100%)',
    borderRadius: '20px',
    border: '1px solid rgba(255,255,255,0.2)',
    backdropFilter: isFirefox ? 'blur(10px)' : 'blur(20px)',
    WebkitBackdropFilter: isFirefox ? 'blur(10px)' : 'blur(20px)',
    boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.1),
      0 4px 16px rgba(0, 0, 0, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 0.3),
      inset 0 0 20px rgba(255, 255, 255, 0.05)
    `,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: `
        0 12px 40px rgba(0, 0, 0, 0.15),
        0 6px 20px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.4),
        inset 0 0 24px rgba(255, 255, 255, 0.08)
      `,
    }
  };

  const sectionHeaderSx = {
    fontWeight: 700,
    color: '#3F51B5',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 3,
    fontSize: '1.2rem'
  };

  // Charger les paramètres
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const appSettings = await getAppSettings();
        
        // Initialiser avec des valeurs par défaut si assemblyAISettings n'existe pas
        const assemblyAISettings = appSettings?.assemblyAISettings || {};
        
        setSettings({
          enableDiarization: assemblyAISettings.enableDiarization || false,
          enablePunctuation: assemblyAISettings.enablePunctuation || true,
          enableFormatting: assemblyAISettings.enableFormatting || true
        });
      } catch (err) {
        addSystemNotification('Erreur lors du chargement des paramètres:', 'error');
        setError("Impossible de charger les paramètres de transcription.");
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setOperationStatus({
      success: false,
      error: false,
      message: ''
    });
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Récupérer les paramètres actuels
      const currentAppSettings = await getAppSettings();
      
      // Mettre à jour uniquement les paramètres AssemblyAI
      const updatedSettings = {
        ...currentAppSettings,
        assemblyAISettings: {
          ...currentAppSettings.assemblyAISettings,
          enableDiarization: settings.enableDiarization,
          enablePunctuation: settings.enablePunctuation,
          enableFormatting: settings.enableFormatting
        }
      };
      
      await updateAppSettings(updatedSettings);
      
      setOperationStatus({
        success: true,
        error: false,
        message: "Les paramètres ont été enregistrés avec succès."
      });
      
      showAlert?.('Paramètres de transcription sauvegardés avec succès !', 'success');
    } catch (err) {
      addSystemNotification('Erreur lors de l\'opération', 'error');
      setOperationStatus({
        success: false,
        error: true,
        message: "Erreur: Impossible d'enregistrer les paramètres."
      });
    } finally {
      setSaving(false);
      
      // Fermer l'alerte après 5 secondes
      setTimeout(() => {
        setOperationStatus({
          success: false,
          error: false,
          message: ''
        });
      }, 5000);
    }
  };

  // Fonction pour ouvrir le dialogue de test d'email
  const handleTestEmailClick = () => {
    if (currentUser?.email) {
      setTestEmailAddress(currentUser.email);
    }
    setTestEmailDialogOpen(true);
  };

  // Fonction pour fermer le dialogue de test d'email
  const handleTestEmailClose = () => {
    setTestEmailDialogOpen(false);
    setTestEmailStatus({
      success: false,
      error: false,
      message: ''
    });
  };

  // Fonction pour envoyer l'email de test
  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      setTestEmailStatus({
        success: false,
        error: true,
        message: 'Veuillez renseigner une adresse email de destination'
      });
      return;
    }

    try {
      setTestEmailLoading(true);
      setTestEmailStatus({ success: false, error: false, message: '' });

      // Utiliser le service email pour envoyer le test
      await emailService.sendTestEmail(testEmailAddress);

      setTestEmailStatus({
        success: true,
        error: false,
        message: 'Email de test envoyé avec succès ! Vérifiez votre boîte de réception.'
      });
    } catch (error) {
      addSystemNotification('Erreur lors de l\'opération', 'error');
      setTestEmailStatus({
        success: false,
        error: true,
        message: `Erreur: ${error.message}`
      });
    } finally {
      setTestEmailLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 400,
        ...glassBoxSx,
        m: 3
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2, color: '#3F51B5' }} />
          <Typography variant="h6" color="#3F51B5" fontWeight={600}>
            Chargement des paramètres...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          m: 3,
          borderRadius: '12px',
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
          ...glassBoxSx
        }}
      >
        ❌ {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête */}
      <Box sx={{ 
        ...glassBoxSx,
        mb: 3,
        p: 3,
        background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.1) 0%, rgba(92, 107, 192, 0.08) 100%)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{
                p: 1.5,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
                boxShadow: '0 6px 20px rgba(63, 81, 181, 0.3)',
              }}>
                <SettingsIcon sx={{ fontSize: 28, color: 'white' }} />
              </Box>
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3F51B5',
                    mb: 0.5
                  }}
                >
                  ⚙️ Paramètres de Transcription
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configurez les options de transcription automatique avec AssemblyAI
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Réinitialiser aux valeurs par défaut">
              <IconButton 
                onClick={() => {
                  // Réinitialiser aux paramètres par défaut
                  localStorage.removeItem('transcriptionSettings');
                  window.location.reload();
                }}
                disabled={loading || saving}
                sx={{
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(8px)',
                  color: '#3F51B5',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.25)',
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <RestoreIcon />
              </IconButton>
            </Tooltip>
            
            <BaseButton
              onClick={saveSettings}
              disabled={!settings || saving}
              loading={saving}
              startIcon={!saving ? <SaveIcon /> : undefined}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 100%)',
                },
                '&:disabled': {
                  background: 'rgba(63, 81, 181, 0.2)',
                  color: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </BaseButton>
          </Box>
        </Box>
      </Box>

      {/* Messages d'état */}
      {operationStatus.success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3, 
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)'
          }}
        >
          ✅ {operationStatus.message}
        </Alert>
      )}

      {operationStatus.error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)'
          }}
        >
          ❌ {operationStatus.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Identification des locuteurs */}
        <Grid item xs={12} md={6}>
          <Box sx={glassBoxSx}>
            <Typography variant="h6" sx={sectionHeaderSx}>
              <RecordVoiceOverIcon />
              Identification des locuteurs
            </Typography>

            <Stack spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.enableDiarization || false}
                    onChange={(e) => handleSettingChange('enableDiarization', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#3F51B5',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3F51B5',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Diarization (Identification des locuteurs)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Distingue automatiquement les différents intervenants dans la transcription
                    </Typography>
                  </Box>
                }
              />

              <Box sx={{
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(145deg, rgba(63, 81, 181, 0.05) 0%, rgba(92, 107, 192, 0.08) 100%)',
                border: '1px solid rgba(63, 81, 181, 0.2)',
                backdropFilter: 'blur(5px)'
              }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#3F51B5', mb: 1 }}>
                  ℹ️ Comment ça marche ?
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Quand la diarization est activée, l'IA analyse les caractéristiques vocales pour identifier 
                  les différents locuteurs. La transcription affichera alors "Locuteur 1:", "Locuteur 2:", etc. 
                  avant chaque intervention. Cette fonctionnalité est particulièrement utile pour les réunions 
                  avec plusieurs participants.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>

        {/* Formatage du texte */}
        <Grid item xs={12} md={6}>
          <Box sx={glassBoxSx}>
            <Typography variant="h6" sx={sectionHeaderSx}>
              <EditIcon />
              Formatage du texte
            </Typography>

            <Stack spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.enablePunctuation || false}
                    onChange={(e) => handleSettingChange('enablePunctuation', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#3F51B5',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3F51B5',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Ponctuation automatique
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ajoute automatiquement les virgules, points et autres signes de ponctuation
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.enableFormatting || false}
                    onChange={(e) => handleSettingChange('enableFormatting', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#3F51B5',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3F51B5',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Formatage du texte
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Améliore la mise en forme générale du texte (majuscules, espacement, etc.)
                    </Typography>
                  </Box>
                }
              />

              <Box sx={{
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(145deg, rgba(63, 81, 181, 0.05) 0%, rgba(92, 107, 192, 0.08) 100%)',
                border: '1px solid rgba(63, 81, 181, 0.2)',
                backdropFilter: 'blur(5px)'
              }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#3F51B5', mb: 1 }}>
                  💡 Recommandation
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Il est recommandé d'activer la ponctuation et le formatage pour obtenir 
                  des transcriptions plus lisibles et professionnelles. Ces options améliorent 
                  significativement la qualité du texte final.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>

        {/* Informations importantes */}
        <Grid item xs={12}>
          <Box sx={glassBoxSx}>
            <Typography variant="h6" sx={sectionHeaderSx}>
              <MicIcon />
              Informations importantes
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(145deg, rgba(76, 175, 80, 0.1) 0%, rgba(129, 199, 132, 0.08) 100%)',
                  border: '1px solid rgba(76, 175, 80, 0.2)',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ color: '#4CAF50', mb: 1 }}>🎯</Typography>
                  <Typography variant="body2" fontWeight={600} color="#4CAF50" gutterBottom>
                    Précision
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    AssemblyAI utilise l'intelligence artificielle pour une transcription de haute qualité
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(145deg, rgba(33, 150, 243, 0.1) 0%, rgba(100, 181, 246, 0.08) 100%)',
                  border: '1px solid rgba(33, 150, 243, 0.2)',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ color: '#2196F3', mb: 1 }}>🌍</Typography>
                  <Typography variant="body2" fontWeight={600} color="#2196F3" gutterBottom>
                    Multilingue
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Support optimisé pour le français avec reconnaissance des accents régionaux
                  </Typography>
                </Box>
              </Grid>

            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* Dialog de test d'email */}
      <BaseDialog
        open={testEmailDialogOpen}
        onClose={handleTestEmailClose}
        title="📧 Test d'envoi d'email"
        subtitle="Vérifiez votre configuration email"
        maxWidth="sm"
        customBackgroundContent="#f8faff"
        actions={
          <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
            <BaseButton 
              onClick={handleTestEmailClose}
              variant="outlined"
              disabled={testEmailLoading}
              sx={{ px: 4, py: 1 }}
            >
              Annuler
            </BaseButton>
            <BaseButton 
              onClick={handleSendTestEmail}
              variant="contained"
              disabled={!testEmailAddress || testEmailLoading}
              startIcon={testEmailLoading ? <CircularProgress size={20} /> : <EmailIcon />}
              sx={{ px: 4, py: 1 }}
            >
              {testEmailLoading ? 'Envoi...' : 'Envoyer le test'}
            </BaseButton>
          </Box>
        }
      >
        <Box sx={{ 
          py: 2,
          background: 'linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(248,250,255,0.9) 100%)',
          borderRadius: '20px',
          border: '1px solid rgba(63, 81, 181, 0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          {testEmailStatus.success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {testEmailStatus.message}
            </Alert>
          )}
          
          {testEmailStatus.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {testEmailStatus.message}
            </Alert>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Envoyez un email de test depuis votre adresse email connectée pour vérifier la configuration.
          </Typography>
          
          <BaseTextField
            autoFocus
            label="Adresse email de test"
            type="email"
            fullWidth
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            helperText="L'email de test sera envoyé à cette adresse"
            disabled={testEmailLoading}
          />
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note :</strong> Cette fonctionnalité nécessite un token Azure valide. 
              Assurez-vous d'être connecté avec un compte qui a les permissions d'envoi d'email.
            </Typography>
          </Alert>
        </Box>
      </BaseDialog>
    </Box>
  );
};

export default TranscriptionSettings; 